import { FC, ReactElement, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import DatePicker from 'react-datepicker';
import { FormattedMessage, useIntl } from 'react-intl';

import 'react-datepicker/dist/react-datepicker.css';

import styles from './priorityPicker.module.scss';

import {
  EducationPriorityApi,
  EducationPriorityLevel,
  getLocalIsoDate,
  IEducationPriority,
  parseLocalIsoDate,
} from 'services/education-priority';
import { formatDate } from 'utils/formatDate';
import { Button } from 'views/components/UI/Button';
import { Headline4 } from 'views/components/UI/Headline';
import { ArrowLeft, ArrowRight } from 'views/components/UI/Icons/Icons';
import { IIconProps } from 'views/components/UI/Icons/icons.interface';
import { PriorityCritical } from 'views/components/UI/Icons/PriorityCritical';
import { PriorityMajor } from 'views/components/UI/Icons/PriorityMajor';
import { PriorityMinor } from 'views/components/UI/Icons/PriorityMinor';
import { PriorityTrivial } from 'views/components/UI/Icons/PriorityTrivial';
import { PriorityUnset } from 'views/components/UI/Icons/PriorityUnset';
import { Menu } from 'views/components/UI/Menu';
import { useDialogBusy } from 'views/components/UI/Modal';
import { StepMenu } from 'views/components/UI/StepMenu';
import { Text } from 'views/components/UI/Text';
import { Tooltip } from 'views/components/UI/Tooltip';

interface IPriorityPickerProps {
  accountId: number;
  knowledgeAreaId: number;
  priority: IEducationPriority | null;
  readOnly: boolean;
  className?: string;
  iconOnly?: boolean;
  hideUnsetIcon?: boolean;
  /** Use `tertiary` when the picker sits in a table. */
  mode?: 'secondary' | 'tertiary';
}

const PRIORITY_LEVELS: EducationPriorityLevel[] = [
  'CRITICAL',
  'MAJOR',
  'MINOR',
  'TRIVIAL',
];

const PRIORITY_ICON: Record<EducationPriorityLevel, FC<IIconProps>> = {
  CRITICAL: PriorityCritical,
  MAJOR: PriorityMajor,
  MINOR: PriorityMinor,
  TRIVIAL: PriorityTrivial,
};

const PRIORITY_LABEL_ID: Record<EducationPriorityLevel, string> = {
  TRIVIAL: 'education-priority.level.trivial',
  MINOR: 'education-priority.level.minor',
  MAJOR: 'education-priority.level.major',
  CRITICAL: 'education-priority.level.critical',
};

const UNSET_LABEL_ID = 'education-priority.level.unset';

const PriorityPicker: FC<IPriorityPickerProps> = ({
  accountId,
  knowledgeAreaId,
  priority,
  readOnly,
  className,
  iconOnly,
  hideUnsetIcon,
  mode = 'secondary',
}): ReactElement => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const [pendingLevel, setPendingLevel] = useState<EducationPriorityLevel | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const pendingRef = useRef(false);
  const isMountedRef = useRef(true);
  useDialogBusy(isSaving);

  useEffect(
    () => (): void => {
      isMountedRef.current = false;
    },
    [],
  );

  const closePicker = (): void => {
    if (pendingRef.current) return;
    setAnchorEl(null);
    setPendingLevel(null);
  };

  const onLevelClick = (level: EducationPriorityLevel): void => {
    setPendingLevel(level);
    setErrorMessage('');
  };

  const onBackClick = (): void => {
    setPendingLevel(null);
    setErrorMessage('');
  };

  const onUnsetClick = async (): Promise<void> => {
    if (!priority) {
      return;
    }

    if (pendingRef.current) return;
    pendingRef.current = true;
    setIsSaving(true);
    setErrorMessage('');
    try {
      await new EducationPriorityApi().remove(priority.id);
      window.dispatchEvent(new Event('education-priority-updated'));

      if (isMountedRef.current) {
        setAnchorEl(null);
        setPendingLevel(null);
      }
    } catch (err) {
      if (isMountedRef.current)
        setErrorMessage('education-priority.modal.error.request-failed');
    } finally {
      pendingRef.current = false;
      if (isMountedRef.current) setIsSaving(false);
    }
  };

  const onDueDateChange = async (date: Date | null): Promise<void> => {
    if (!pendingLevel) {
      return;
    }

    const dueDate = date ? getLocalIsoDate(date) : null;

    if (pendingRef.current) return;
    pendingRef.current = true;
    setIsSaving(true);
    setErrorMessage('');
    try {
      if (priority) {
        await new EducationPriorityApi().update(priority.id, accountId, {
          knowledgeAreaId,
          dueDate,
          priority: pendingLevel,
        });
      } else {
        await new EducationPriorityApi().create({
          accountId,
          knowledgeAreaId,
          dueDate,
          priority: pendingLevel,
        });
      }

      window.dispatchEvent(new Event('education-priority-updated'));

      if (isMountedRef.current) {
        setAnchorEl(null);
        setPendingLevel(null);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setErrorMessage('education-priority.modal.error.request-failed');
      }
    } finally {
      pendingRef.current = false;
      if (isMountedRef.current) setIsSaving(false);
    }
  };

  const TriggerIcon = priority ? PRIORITY_ICON[priority.priority] : PriorityUnset;
  const triggerLabelId = priority ? PRIORITY_LABEL_ID[priority.priority] : UNSET_LABEL_ID;
  const PendingLevelIcon = pendingLevel ? PRIORITY_ICON[pendingLevel] : PriorityUnset;
  const dueDateSuffix = priority?.dueDate ? ` (${formatDate(priority.dueDate)})` : '';

  const triggerLabel = intl.formatMessage({ id: triggerLabelId }) + dueDateSuffix;
  const showTriggerIcon = !hideUnsetIcon || Boolean(priority);
  if (readOnly) {
    if (iconOnly) {
      return (
        <Tooltip content={triggerLabel}>
          <TriggerIcon size="16px" />
        </Tooltip>
      );
    }

    return (
      <span className={styles.readOnly}>
        {showTriggerIcon && <TriggerIcon size="16px" />}
        <Text
          variant="body"
          color={priority ? 'var(--sk-text-default)' : 'var(--sk-text-subtle)'}
        >
          <FormattedMessage id={triggerLabelId} />
          {dueDateSuffix}
        </Text>
      </span>
    );
  }

  const trigger = (
    <Button
      mode={mode}
      color="dark"
      className={priority ? undefined : styles.unset}
      icon={showTriggerIcon || iconOnly ? <TriggerIcon size="16px" /> : undefined}
      aria-label={triggerLabel}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      disabled={isSaving}
      onClick={event => {
        if (pendingRef.current) return;
        if (anchorEl) closePicker();
        else {
          setAnchorEl(event.currentTarget);
          setPendingLevel(null);
          setErrorMessage('');
        }
      }}
    >
      {iconOnly ? undefined : triggerLabel}
    </Button>
  );

  return (
    <div className={classNames(styles.picker, className)}>
      {iconOnly ? (
        <Tooltip content={triggerLabel} interactiveChild hideBubble={isOpen}>
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
      {anchorEl && (
        <StepMenu
          anchorEl={anchorEl}
          onClose={closePicker}
          busy={isSaving}
          title={
            pendingLevel ? (
              <span className={styles.readOnly}>
                <PendingLevelIcon size="16px" />
                <FormattedMessage id={PRIORITY_LABEL_ID[pendingLevel]} />
              </span>
            ) : (
              intl.formatMessage({ id: 'education-priority.picker.title' })
            )
          }
          onBack={pendingLevel ? onBackClick : undefined}
        >
          {pendingLevel ? (
            <div className={styles.calendarArea}>
              <Text variant="bodySmall" color="var(--sk-text-subtle)">
                <FormattedMessage id="education-priority.picker.due-date-title" />
              </Text>
              <DatePicker
                inline
                selected={priority?.dueDate ? parseLocalIsoDate(priority.dueDate) : null}
                onChange={onDueDateChange}
                minDate={priority ? undefined : new Date()}
                calendarStartDay={1}
                filterDate={() => !isSaving}
                calendarClassName={styles.calendar}
                renderCustomHeader={({
                  date,
                  decreaseMonth,
                  increaseMonth,
                  prevMonthButtonDisabled,
                  nextMonthButtonDisabled,
                }) => (
                  <div className={styles.calendarHeader}>
                    <Button
                      mode="tertiary"
                      color="dark"
                      icon={<ArrowLeft />}
                      onClick={decreaseMonth}
                      disabled={prevMonthButtonDisabled || isSaving}
                      aria-label={intl.formatMessage({
                        id: 'education-priority.picker.previous-month',
                      })}
                    />
                    <Headline4 inline>
                      {intl.formatDate(date, { month: 'long', year: 'numeric' })}
                    </Headline4>
                    <Button
                      mode="tertiary"
                      color="dark"
                      icon={<ArrowRight />}
                      onClick={increaseMonth}
                      disabled={nextMonthButtonDisabled || isSaving}
                      aria-label={intl.formatMessage({
                        id: 'education-priority.picker.next-month',
                      })}
                    />
                  </div>
                )}
              />
              <Button
                mode="secondary"
                color="dark"
                modBlock
                className={styles.noDueDate}
                onClick={() => onDueDateChange(null)}
                disabled={isSaving}
              >
                <FormattedMessage id="education-priority.picker.no-due-date" />
              </Button>
            </div>
          ) : (
            <Menu.Items
              aria-label={intl.formatMessage({ id: 'education-priority.picker.title' })}
            >
              {PRIORITY_LEVELS.map(level => {
                const Icon = PRIORITY_ICON[level];
                return (
                  <Menu.Item
                    key={level}
                    isDisable={isSaving}
                    onClick={() => onLevelClick(level)}
                  >
                    <span className={styles.readOnly}>
                      <Icon size="16px" />
                      <FormattedMessage id={PRIORITY_LABEL_ID[level]} />
                    </span>
                  </Menu.Item>
                );
              })}
              {priority && (
                <Menu.Item isDisable={isSaving} onClick={onUnsetClick}>
                  <span className={styles.readOnly}>
                    <PriorityUnset size="16px" />
                    <FormattedMessage id={UNSET_LABEL_ID} />
                  </span>
                </Menu.Item>
              )}
            </Menu.Items>
          )}
          {errorMessage && (
            <div role="alert">
              <Text variant="bodySmall" color="var(--sk-text-danger)">
                <FormattedMessage id={errorMessage} />
              </Text>
            </div>
          )}
        </StepMenu>
      )}
    </div>
  );
};

export { PriorityPicker };
